import React, { useState, useEffect, useCallback } from 'react'
import { Button, ControlledTooltip, DimmerLoader, Header, Heading, Label, Modal, Table } from 'views/components'
import styles from './DocumentInfo.module.scss'
import { useSelector } from 'react-redux'
import { userApiSelectors } from 'modules/api/user'
import { partyApiSelectors } from 'modules/api/party'
import UppyWrapper from 'modules/documents/UppyWrapper'
import {
  invoiceFileTypes,
  naturalPersonDocumentTypes,
  entityDocumentTypes,
  leaseFileTypes,
  propertyFileTypes,
} from 'modules/documents/constants'
import { dateUtils } from 'utils/index'
import { useDocumentInfo } from './useDocumentInfo'
import { formatFileType } from './utils'
import { documentsApiUtils } from 'modules/api/documents'
import { IDocument } from 'modules/api/documents/types'

type TProps = {
  className: string
  classId: string
  modalTitle?: string
}

interface Document extends IDocument {
  isEditing?: boolean
  tags?: Record<string, string>
}

const DocumentInfo = ({ className, classId, modalTitle }: TProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isReadOnly = useSelector(userApiSelectors.isReadOnlyRole)
  const getPartyType = useSelector((state: any) => partyApiSelectors.getPartyTypeById(state)(classId))

  // Use the custom hook for document management
  const {
    documents,
    documentTags,
    removing,
    readFileListError,
    onSaveError,
    fetchDocumentTagsError,
    refreshDocuments,
    downloadDocument,
    onRemove,
    onEdit,
    onNameChange,
    onTagsChange,
    onSave,
  } = useDocumentInfo(className, classId, isReadOnly)

  // Function to get appropriate tag options based on partyType
  const getTagOptions = () => {
    if (className === 'invoice') {
      return invoiceFileTypes
    } else if (className === 'lease') {
      return leaseFileTypes
    } else if (className === 'property') {
      return propertyFileTypes
    } else if (className === 'party') {
      if (getPartyType === 'person') {
        return naturalPersonDocumentTypes
      } else if (getPartyType === 'company') {
        return entityDocumentTypes
      } else {
        // Default to natural person if partyType is not specified or unknown
        return naturalPersonDocumentTypes
      }
    } else {
      // Default to lease file types if forOwner.className is not recognized
      return leaseFileTypes
    }
  }

  // Load documents on component mount and when classId changes
  useEffect(() => {
    refreshDocuments()
  }, [refreshDocuments])

  // ActionButton component for file actions
  const ActionButton = ({ tooltip, icon, action, disabled = false }: any): React.ReactElement => (
    <ControlledTooltip body={tooltip} place="above">
      <Button disabled={disabled} ghost className={styles[icon]} onClick={action} />
    </ControlledTooltip>
  )

  // Handle download (alias for existing downloadDocument function)
  const onDownload = useCallback(
    (file: Document) => {
      return downloadDocument(file)
    },
    [downloadDocument],
  )

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const formatDataFile = (file: any): any => {
    const { id, name, type, isEditing, attached_at, updated_at } = file

    const attachedAt = new Date(attached_at).toDateString().replace(/^\(*\)/g, '')
    const tags = file.tags || documentTags[id] || {}

    return {
      id,
      fileName: (
        <span className={styles['file-name']}>
          {isEditing === true ? (
            <input
              className={styles['file-name-input']}
              defaultValue={name}
              onChange={onNameChange.bind(null, file)}
              readOnly={false}
            />
          ) : onSaveError ? (
            <span className={styles['error-message']}>Unable to update document</span>
          ) : (
            <strong className={styles.filename}>{name}</strong>
          )}
        </span>
      ),
      fileType: <span className={styles['file-type']}>{formatFileType(type)}</span>,
      tags: (
        <span className={styles['file-tags']}>
          {isEditing === true ? (
            <select
              className={styles['file-tags-select']}
              value={tags && Object.keys(tags).length > 0 ? Object.keys(tags)[0] : ''}
              onChange={onTagsChange.bind(null, file)}
            >
              <option value="">--Select Tag--</option>
              {getTagOptions().map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          ) : tags && Object.keys(tags).length > 0 ? (
            <div className={styles['tags-container']}>
              {Object.values(tags).map((tag, index) => (
                <Label circular className={styles['tag-chip']} key={index}>
                  {String(tag)
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase())}
                </Label>
              ))}
            </div>
          ) : fetchDocumentTagsError ? (
            <span className={styles['no-tags']}>Unable to fetch tags</span>
          ) : (
            <span className={styles['no-tags']}>No tags</span>
          )}
        </span>
      ),
      timeDetails: (
        <span className={'time-details'}>
          <strong className={styles['timestring-below']}>{attachedAt}</strong>
          <span className={styles['timeago-custom-style-below']}>
            {updated_at !== undefined ? `Updated ${dateUtils.timeAgo(updated_at)}` : '-'}
          </span>
        </span>
      ),
      actions: (
        <span className={styles.actions}>
          {allowRenaming ? (
            isEditing !== true ? (
              <ActionButton tooltip="Edit" icon="edit" action={() => onEdit(file)} />
            ) : (
              <ActionButton tooltip="Save" icon="save" action={async () => await onSave(file)} />
            )
          ) : null}
          <ActionButton tooltip="Download" icon="download" action={async () => await onDownload(file)} />
          {allowRemove ? (
            <ActionButton tooltip="Remove" icon="remove" action={async () => await onRemove(file.id)} />
          ) : null}
        </span>
      ),
    }
  }

  // Configuration variables for formatDataFile
  const allowRenaming = !isReadOnly // Allow renaming if not read-only
  const allowRemove = !isReadOnly // Allow removal if not read-only

  const cols = [
    {
      Header: 'File',
      accessor: 'fileName',
      style: { width: '30%', minWidth: '200px' },
    },
    {
      Header: 'Type',
      accessor: 'tags',
      style: { width: '20%', minWidth: '150px' },
    },
    {
      Header: 'Format',
      accessor: 'fileType',
      style: { width: '15%', minWidth: '120px' },
    },
    {
      Header: 'Uploaded',
      accessor: 'timeDetails',
      style: { width: '20%', minWidth: '150px' },
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      style: { width: '15%', minWidth: '100px', justifyContent: 'flex-end' },
    },
  ]

  return (
    <div className={styles.root}>
      <Heading as="h3">
        {documents.length < 1
          ? 'No Documents'
          : `${documents.length} ${documents.length === 1 ? 'Document' : 'Documents'}`}
      </Heading>
      <div className={styles.summary}>
        <UppyWrapper
          forOwner={{ className, classId }}
          partyType={getPartyType}
          onComplete={() => refreshDocuments()}
          parentProps={{
            authToken: documentsApiUtils.getAuthToken(),
            documentServiceEndpoint: documentsApiUtils.getDocumentServiceEndpoint(),
          }}
        />
        {documents.length > 0 && (
          <Button size="sm" onClick={handleOpenModal} style={{ marginLeft: '10px' }} disabled={!!readFileListError}>
            View all documents
          </Button>
        )}
      </div>

      <Modal
        width="xl"
        padding="sm"
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        className={styles['document-modal']}
      >
        <Header className={styles.header} tag="h4" text={modalTitle ? `Documents for ${modalTitle}` : 'Documents'} />
        <Table type="default">
          <Table.Head>
            <Table.Row>
              {cols.map((col: any, i: any) => (
                <Table.Cell key={i} head style={col.style || {}}>
                  {col.Header}
                </Table.Cell>
              ))}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {documents
              ?.map((file: any) => formatDataFile(file))
              .map((file: any, i: number) => (
                <DimmerLoader
                  key={`file-container-${i}`}
                  loading={removing.includes(file.id as never)}
                  bgColor={'appBg'}
                >
                  <Table.Row key={i}>
                    <Table.Cell style={{ paddingLeft: '16px', paddingRight: '16px' }}>{file.fileName}</Table.Cell>
                    <Table.Cell style={{ paddingLeft: '8px', paddingRight: '0px' }}>{file.tags}</Table.Cell>
                    <Table.Cell style={{ paddingLeft: '8px', paddingRight: '0px' }}>{file.fileType}</Table.Cell>
                    <Table.Cell style={{ paddingLeft: '8px', paddingRight: '0px' }}>{file.timeDetails}</Table.Cell>
                    <Table.Cell style={{ paddingLeft: '0px', paddingRight: '0px', justifyContent: 'flex-end' }}>
                      {file.actions}
                    </Table.Cell>
                  </Table.Row>
                </DimmerLoader>
              ))}
          </Table.Body>
        </Table>
      </Modal>
    </div>
  )
}

export default DocumentInfo
